import React, { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import authService from '../services/authService'

interface ResetPasswordForm {
  password: string
  confirmPassword: string
}

const ResetPassword: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { register, handleSubmit, watch, formState: { errors } } = useForm<ResetPasswordForm>()

  const password = watch('password')

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) {
      toast.error('Invalid reset link')
      return
    }
    try {
      setIsLoading(true)
      await authService.resetPassword(token, data.password)
      setSuccess(true)
      toast.success('Password reset successfully!')
      setTimeout(() => navigate('/login'), 2000)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reset password. The link may have expired.')
    } finally {
      setIsLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center pt-20 pb-12 px-4">
        <div className="glass-strong rounded-2xl p-8 max-w-md w-full text-center">
          <h2 className="font-display text-display-2 font-bold text-foreground mb-4">Invalid reset link</h2>
          <p className="text-muted-foreground mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password" className="text-primary hover:underline">
            Request a new reset link
          </Link>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center pt-20 pb-12 px-4">
        <div className="glass-strong rounded-2xl p-8 max-w-md w-full text-center">
          <h2 className="font-display text-2xl font-bold text-foreground mb-4">Password reset successfully</h2>
          <p className="text-muted-foreground mb-6">Redirecting you to sign in...</p>
          <Link to="/login" className="text-primary hover:underline">
            Go to Sign in
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center pt-20 pb-12 px-4">
      <div className="max-w-md w-full">
        <div className="glass-strong rounded-2xl p-8 sm:p-10">
          <div className="text-center mb-8">
            <h1 className="font-display text-display-1 font-bold mb-2 text-foreground">Set new password</h1>
            <p className="text-body-lg text-muted-foreground">Enter your new password below</p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="password" className="text-nav font-medium mb-2 block text-foreground">New password</label>
              <input
                {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Password must be at least 6 characters' } })}
                type="password"
                className="w-full px-4 py-3 rounded-xl bg-muted/10 border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-body"
                placeholder="Enter new password"
              />
              {errors.password && <p className="mt-2 text-small text-destructive">{errors.password.message}</p>}
            </div>
            <div>
              <label htmlFor="confirmPassword" className="text-nav font-medium mb-2 block text-foreground">Confirm password</label>
              <input
                {...register('confirmPassword', { required: 'Please confirm your password', validate: (value) => value === password || 'Passwords do not match' })}
                type="password"
                className="w-full px-4 py-3 rounded-xl bg-muted/10 border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-body"
                placeholder="Confirm new password"
              />
              {errors.confirmPassword && <p className="mt-2 text-small text-destructive">{errors.confirmPassword.message}</p>}
            </div>

            <button type="submit" disabled={isLoading} className="w-full py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold text-nav hover:opacity-90 transition-all glow-subtle disabled:opacity-50 disabled:cursor-not-allowed">
              {isLoading ? 'Resetting...' : 'Reset password'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-nav text-muted-foreground hover:text-foreground transition-colors">
              Back to Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword
